// Hierarchical gameplay tags and tag queries, after Unreal's gameplay tags (decision 0050): a tag is a dotted path
// ("state.stunned", "damage.fire"); a query for "state" matches "state.stunned" unless it asks for an exact match.
// One query shape (all, any, none, and nested groups) serves abilities, effects and, later, quests and dialogue.

/** A dotted, hierarchical tag ("state.stunned.hard"). */
export type Tag = string;

/** Whether `have` is `want` or one of its children ("state.stunned" matches a query for "state"). */
export function tagMatches(have: Tag, want: Tag, exact = false): boolean {
  if (have === want) return true;
  return !exact && have.length > want.length && have.startsWith(want) && have.charCodeAt(want.length) === 46; // "."
}

/** Whether any tag in `tags` matches `want`. */
export function hasTag(tags: readonly Tag[], want: Tag, exact = false): boolean {
  for (const t of tags) if (tagMatches(t, want, exact)) return true;
  return false;
}

/**
 * A tag query (Unreal's FGameplayTagQuery, cut down): every tag in `all` present, at least one of `any` (when given),
 * none of `none`; then every group in `allOf`, at least one of `anyOf`, and no group of `noneOf`. Matching is
 * hierarchical unless `exact`. An empty query matches anything.
 */
export interface TagQuery {
  all?: readonly Tag[];
  any?: readonly Tag[];
  none?: readonly Tag[];
  exact?: boolean;
  allOf?: readonly TagQuery[];
  anyOf?: readonly TagQuery[];
  noneOf?: readonly TagQuery[];
}

/** Whether `tags` satisfy the query. */
export function matchTags(tags: readonly Tag[], q: TagQuery | undefined): boolean {
  if (!q) return true;
  const ex = q.exact === true;
  if (q.all && !q.all.every((t) => hasTag(tags, t, ex))) return false;
  if (q.any && q.any.length && !q.any.some((t) => hasTag(tags, t, ex))) return false;
  if (q.none && q.none.some((t) => hasTag(tags, t, ex))) return false;
  if (q.allOf && !q.allOf.every((g) => matchTags(tags, g))) return false;
  if (q.anyOf && q.anyOf.length && !q.anyOf.some((g) => matchTags(tags, g))) return false;
  if (q.noneOf && q.noneOf.some((g) => matchTags(tags, g))) return false;
  return true;
}
